import {
  renderFormattedDate,
  renderDataGridLocaleText,
  prepareActiveUserOptions,
  normalizeProjectCompanyCodes,
  getProductBankValidationMessage,
  findBookProductBankValidationIssue,
} from 'utils';
import {
  EFFORT_MAIL_CONTACT_TYPE_ENUM,
  PROJECT_STEP_PRIORITY_TYPE_ENUM,
  getEffortMailContactTypeTranslated,
} from 'enums';
import type {
  BookToBeCreated,
  CompanyToBeCreated,
  ProductToBeCreated,
  BookLongTextFieldName,
  RelatedContactToBeCreated,
} from './types';
import {
  StyledBox,
  EditToolbar,
  ZebraDataGrid,
  RenderEditItem,
  LoadingBackdrop,
  GridEditDateCell,
  GridFilterDateInput,
  ProductsCreateTable,
  RelatedContactsCreateTable,
} from 'components';
import {
  Box,
  Alert,
  Radio,
  Paper,
  Stack,
  Button,
  Collapse,
  TextField,
  FormLabel,
  Typography,
  IconButton,
  RadioGroup,
  Grid2 as Grid,
  FormControlLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  GridRowId,
  GridColDef,
  GridRowModes,
  GridRowsProp,
  GridRowModel,
  GridColTypeDef,
  GridEventListener,
  GridRowModesModel,
  GRID_DATE_COL_DEF,
  GridActionsCellItem,
  getGridDateOperators,
  GridRowEditStopReasons,
  GridPreProcessEditCellProps,
} from '@mui/x-data-grid-premium';
import { useLookup } from 'hooks';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import BookPanel from './components/book-panel';
import {
  selectConnectionTypes,
  selectConnectionTypesIsEmpty,
  selectConnectionTypesLoading,
} from 'store/features/connection-types/selector';
import CloseIcon from '@mui/icons-material/Close';
import { ROOT_ROUTE_PATH } from 'constants/routes';
import CancelIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { ConnectionType, GibType, User } from 'library/types';
import BookAutocomplete from './components/book-autocomplete';
import gibTypesActions from 'store/features/gib-types/actions';
import ModernBookHeader from './components/modern-book-header';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import ModernBookSection from './components/modern-book-section';
import usedBanksActions from 'store/features/used-banks/actions';
import BookTextAreaField from './components/book-text-area-field';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import BookQuickNavigation from './components/book-quick-navigation';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import connectionTypesActions from 'store/features/connection-types/actions';
import environmentTypesActions from 'store/features/environment-types/actions';
import { modernBookActionsSx, modernBookSectionSx } from './modern-book-styles';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { selectUsedBanks, selectUsedBanksIsEmpty } from 'store/features/used-banks/selector';
import { useMemo, useState, useEffect, useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { selectGibTypes, selectGibTypesIsEmpty, selectGibTypesLoading } from 'store/features/gib-types/selector';
import { selectEnvironmentTypes, selectEnvironmentTypesIsEmpty } from 'store/features/environment-types/selector';

export type { BookToBeCreated, CompanyToBeCreated, ProductToBeCreated, RelatedContactToBeCreated } from './types';

const initialState: BookToBeCreated = {
  companyName: '',
  projects: [],
  connectTypeId: null,
  gibTypeId: null,
  companies: [],
  apiInformation: '',
  vpnInformation: '',
  serviceApiInformation: '',
  rfcUserInformation: '',
  serverConnectionInformation: '',
  serverExternalIp: '',
  financialSeal: '',
  sapDev: '',
  sapTest: '',
  sapProd: '',
  note: '',
  relatedContacts: [],
  effortMailContactType: EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER,
};

const initialRows: GridRowsProp = [];
const initialRowsRelatedContacts: GridRowsProp = [];
const initialRowsProducts: GridRowsProp = [];

const loadActiveUsers = async () => {
  const response = await apiService.getUsers();

  return { ...response, data: response.isSuccess ? prepareActiveUserOptions(response.data) : [] };
};

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  renderEditCell: (params) => {
    return <GridEditDateCell {...params} />;
  },
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponent: GridFilterDateInput,
    InputComponentProps: { showTime: false },
  })),
  valueFormatter: (value) => {
    if (value) {
      return renderFormattedDate(value);
    }

    return '';
  },
};

type LongTextFieldConfig = {
  name: BookLongTextFieldName;
  labelId: string;
  defaultMessage: string;
};

const accessFields: LongTextFieldConfig[] = [
  { name: 'apiInformation', labelId: 'project.apiInformation', defaultMessage: 'API Şifresi' },
  {
    name: 'serviceApiInformation',
    labelId: 'project.serviceApiInformation',
    defaultMessage: 'Sunucu API Bilgileri',
  },
  {
    name: 'rfcUserInformation',
    labelId: 'project.rfcUserInformation',
    defaultMessage: 'RFC Kullanıcı Bilgileri',
  },
  { name: 'vpnInformation', labelId: 'project.vpnInformation', defaultMessage: 'VPN Bilgileri' },
  {
    name: 'serverConnectionInformation',
    labelId: 'project.serverConnectionInformation',
    defaultMessage: 'Sunucu Bağlantı Bilgileri',
  },
  {
    name: 'serverExternalIp',
    labelId: 'project.serverExternalIp',
    defaultMessage: 'Sunucu Dış IP Bilgisi',
  },
  { name: 'financialSeal', labelId: 'project.financialSeal', defaultMessage: 'Mali Mühür' },
];

const sapFields: LongTextFieldConfig[] = [
  { name: 'sapDev', labelId: 'project.sapDev', defaultMessage: 'SAP Dev' },
  { name: 'sapTest', labelId: 'project.sapTest', defaultMessage: 'SAP QA' },
  { name: 'sapProd', labelId: 'project.sapProd', defaultMessage: 'SAP Canlı' },
];

const hasActiveRowEdit = (model: GridRowModesModel) =>
  Object.values(model).some((rowMode) => rowMode.mode === GridRowModes.Edit);

const CreateBook = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { items: users } = useLookup<User>(loadActiveUsers);

  // redux hooks
  const dispatch = useAppDispatch();

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  const connectionTypes = useAppSelector(selectConnectionTypes);
  const loadingConnectionTypes = useAppSelector(selectConnectionTypesLoading);
  const isEmptyConnectionTypes = useAppSelector(selectConnectionTypesIsEmpty);

  const environmentTypes = useAppSelector(selectEnvironmentTypes);
  const isEmptyEnvironmentTypes = useAppSelector(selectEnvironmentTypesIsEmpty);

  const gibTypes = useAppSelector(selectGibTypes);
  const loadingGibTypes = useAppSelector(selectGibTypesLoading);
  const isEmptyGibTypes = useAppSelector(selectGibTypesIsEmpty);

  const usedBanks = useAppSelector(selectUsedBanks);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);

  // useState hooks
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState(initialRows);
  const [loading, setLoading] = useState(false);
  const [rowsProducts, setRowsProducts] = useState(initialRowsProducts);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectedGibType, setSelectedGibType] = useState<GibType | null>(null);
  const [rowModesModelProducts, setRowModesModelProducts] = useState<GridRowModesModel>({});
  const [rowsRelatedContacts, setRowsRelatedContacts] = useState(initialRowsRelatedContacts);
  const [selectedConnectionType, setSelectedConnectionType] = useState<ConnectionType | null>(null);
  const [rowModesModelRelatedContacts, setRowModesModelRelatedContacts] = useState<GridRowModesModel>({});

  // dynamic variables
  const incompleteAdaptation = isEmptyProducts || isEmptyConnectionTypes || isEmptyGibTypes || isEmptyEnvironmentTypes;
  const companiesForProducts = useMemo<CompanyToBeCreated[]>(
    () =>
      rows.map((row) => ({
        code: row.code,
        vkn: row.vkn,
        title: row.title,
        dateOfAnnulment: row.dateOfAnnulment ? dayjs(row.dateOfAnnulment).toISOString() : null,
      })),
    [rows]
  );

  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
      width: 72,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      editable: false,
    },
    {
      field: 'code',
      headerName: intl.formatMessage({ id: 'project.table.code', defaultMessage: 'Şirket Kodu' }),
      flex: 1,
      minWidth: 140,
      editable: true,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'vkn',
      headerName: intl.formatMessage({ id: 'project.table.vkn', defaultMessage: 'Vkn' }),
      flex: 1,
      minWidth: 140,
      editable: true,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'title',
      headerName: intl.formatMessage({ id: 'project.table.title', defaultMessage: 'Ünvan' }),
      flex: 1,
      minWidth: 180,
      editable: true,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'dateOfAnnulment',
      ...dateColumnType,
      headerName: intl.formatMessage({ id: 'project.table.dateOfAnnulment', defaultMessage: 'Fesih Tarihi' }),
      width: 150,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: intl.formatMessage({ id: 'project.table.actions', defaultMessage: 'İşlemler' }),
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label={intl.formatMessage({ id: 'project.table.save', defaultMessage: 'Kaydet' })}
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label={intl.formatMessage({ id: 'project.table.cancel', defaultMessage: 'Vazgeç' })}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={intl.formatMessage({ id: 'project.table.edit', defaultMessage: 'Düzenle' })}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={intl.formatMessage({ id: 'project.table.delete', defaultMessage: 'Sil' })}
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  // form state
  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<BookToBeCreated>({
    defaultValues: initialState,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  // dynamic variables
  const watchedEffortMailContactType = watch('effortMailContactType');

  // async helper functions
  const onSubmit = async (data: BookToBeCreated) => {
    // Empty Company Name control
    if (!data.companyName)
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredCompanyCode" defaultMessage="Firma boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Companies
    const isViewModeForCompanies = !hasActiveRowEdit(rowModesModel);

    if (!isViewModeForCompanies)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForCompanies"
          defaultMessage="Şirketler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of companies model control
    if (data.companies.some((company) => !company.code || !company.vkn || !company.title))
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredFieldsOfCompanies" defaultMessage="Şirket bilgileri boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in companies array control
    if (!data.companies.length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfCompanies"
          defaultMessage="En az bir şirket tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Related Contacts
    const isViewModeForRelatedContacts = !hasActiveRowEdit(rowModesModelRelatedContacts);

    if (!isViewModeForRelatedContacts)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForRelatedContacts"
          defaultMessage="İlgili Kişiler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of related contacts model control
    if (data.relatedContacts.some((relatedContact) => !relatedContact.nameSurname || !relatedContact.email))
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredFieldsOfRelatedContacts"
          defaultMessage="İlgili Kişilerin bilgileri boş bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Email format validation for related contacts
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    if (data.relatedContacts.some((relatedContact) => relatedContact.email !== relatedContact.email.trim()))
      return enqueueSnackbar(
        <FormattedMessage
          id="validations.emailNoWhitespace"
          defaultMessage="E-posta adresinin başında veya sonunda boşluk olamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    if (data.relatedContacts.some((relatedContact) => /[A-Z]/.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage id="validations.emailNoUpperCase" defaultMessage="E-posta adresi büyük harf içeremez!" />,
        {
          variant: 'warning',
        }
      );

    if (data.relatedContacts.some((relatedContact) => /[çÇğĞıİöÖşŞüÜ]/.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage
          id="validations.emailNoTurkishChars"
          defaultMessage="E-posta adresi Türkçe karakter içeremez!"
        />,
        {
          variant: 'warning',
        }
      );

    if (data.relatedContacts.some((relatedContact) => !emailRegex.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage id="validations.invalidEmailFormat" defaultMessage="Geçersiz e-posta formatı" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in related contacts array if effort contact type is only related contacts control
    if (
      watchedEffortMailContactType === EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS &&
      !data.relatedContacts.length
    )
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfRelatedContactsIfEffortContactTypeIsOnlyRelatedContacts"
          defaultMessage="Sadece İlgili Kişiler seçili iken en az bir ilgili kişi tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Products
    const isViewModeForProducts = !hasActiveRowEdit(rowModesModelProducts);

    if (!isViewModeForProducts)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForProducts"
          defaultMessage="Ürünler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of products model control
    if (data.projects.some((project) => !project.id))
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredFieldsOfProducts" defaultMessage="Ürün bilgileri boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in products array control
    if (!data.projects.length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfProjects"
          defaultMessage="En az bir ürün tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // Check that there is no more than one of the same product
    const projectIds = data.projects.map((project) => project.id);
    if (projectIds.filter((item, index) => projectIds.indexOf(item) !== index).length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.shouldNotBeMoreThanOneOfTheSameProduct"
          defaultMessage="Aynı üründen birden fazla olmamalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // Product bank configuration validation
    const productBankValidation = findBookProductBankValidationIssue(data.projects);

    if (productBankValidation) {
      const projectId = productBankValidation.project.id;
      const productTitle =
        products.find((product) => product.id === projectId)?.title ??
        `${intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' })} #${projectId}`;

      return enqueueSnackbar(getProductBankValidationMessage(productBankValidation.issue, productTitle, intl), {
        variant: 'warning',
      });
    }

    const normalizedData: BookToBeCreated = {
      ...data,
      projects: data.projects.map((project) => ({
        ...project,
        companyCodes: normalizeProjectCompanyCodes(project.companyCodes),
      })),
    };

    // Convert empty string values to null
    const realData: BookToBeCreated = Object.entries(normalizedData).reduce((acc, arrEl) => {
      if (arrEl[1] === '') return { ...acc, [arrEl[0]]: null };

      return { ...acc, [arrEl[0]]: arrEl[1] };
    }, initialState);

    // continue
    try {
      setLoading(true);

      const response = await apiService.createBook(realData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage id="project.createSuccess" defaultMessage="Proje başarılı bir şekilde oluşturuldu." />,
          {
            variant: 'success',
          }
        );

        goToMainPage();
      }
    } catch {
      enqueueSnackbar(
        <FormattedMessage
          id="book.modern.createError"
          defaultMessage="Proje oluşturulamadı. Lütfen bilgileri kontrol edip tekrar deneyin."
        />,
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const resetForm = () => {
    setOpen(true);
    setSelectedConnectionType(null);
    setSelectedGibType(null);
    setRows(initialRows);
    setRowModesModel({});
    setRowsProducts(initialRowsProducts);
    setRowModesModelProducts({});
    setRowsRelatedContacts(initialRowsRelatedContacts);
    setRowModesModelRelatedContacts({});

    reset();
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
    setRowModesModel((currentModel) => {
      const nextModel = { ...currentModel };
      delete nextModel[id];
      return nextModel;
    });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const goToMainPage = useCallback(() => navigate(ROOT_ROUTE_PATH), [navigate]);

  const hideAlertBox = useCallback(() => setOpen(false), []);

  const preventGridImplicitSubmit = (event: ReactKeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLElement)) return;
    if (!event.target.closest('.MuiDataGrid-root')) return;

    event.preventDefault();
  };

  // useEffect hooks
  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!connectionTypes.length && !isEmptyConnectionTypes) {
      dispatch(connectionTypesActions.getConnectionTypes());
    }
  }, [connectionTypes, isEmptyConnectionTypes, dispatch]);

  useEffect(() => {
    if (!environmentTypes.length && !isEmptyEnvironmentTypes) {
      dispatch(environmentTypesActions.getEnvironmentTypes());
    }
  }, [environmentTypes, isEmptyEnvironmentTypes, dispatch]);

  useEffect(() => {
    if (!gibTypes.length && !isEmptyGibTypes) {
      dispatch(gibTypesActions.getGibTypes());
    }
  }, [gibTypes, isEmptyGibTypes, dispatch]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  useEffect(() => {
    setValue('companies', companiesForProducts);
  }, [companiesForProducts, setValue]);

  useEffect(() => {
    const formattedRelatedContacts: RelatedContactToBeCreated[] = rowsRelatedContacts.map((row) => ({
      nameSurname: row.nameSurname,
      email: row.email,
      phoneNumber: row.phoneNumber,
    }));

    setValue('relatedContacts', formattedRelatedContacts);
  }, [rowsRelatedContacts, setValue]);

  useEffect(() => {
    const formattedProducts: ProductToBeCreated[] = rowsProducts.map((row) => ({
      id: products.find((product) => product.title === row.projectId)?.id || 0,
      companyCodes: normalizeProjectCompanyCodes(row.companyCodes),
      bankCount: Number(row.bankCount) || 0,
      banks: Array.isArray(row.banks) ? row.banks : [],
      environmentTypeId:
        environmentTypes.find((environmentType) => environmentType.title === row.environmentTypeId)?.id || null,
      liveDate: row.liveDate ? dayjs(row.liveDate).toISOString() : null,
      isOnHold: row.isOnHold || false,
      priority: row.priority || PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
      relatedContactId: row.relatedContactId || null,
      alternativeRelatedContactId: row.alternativeRelatedContactId || null,
      notes: row.notes || '',
    }));

    setValue('projects', formattedProducts);
  }, [rowsProducts, environmentTypes, products, setValue]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, pb: { xs: 4, md: 6 } }}>
      <ModernBookHeader
        companyCount={rows.length}
        contactCount={rowsRelatedContacts.length}
        productCount={rowsProducts.length}
      />

      {incompleteAdaptation ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1, sm: 1.5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            bgcolor: 'background.paper',
          }}>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              border: 0,
              bgcolor: 'transparent',
              '& .MuiAlert-message': { width: '100%' },
            }}>
            <Typography variant="body2">
              <FormattedMessage
                id="general.completeAdaptationsText"
                defaultMessage="Sayfayı görüntülemek için lütfen uyarlamaları tamamlayınız..! (Ürün Grupları, Ürünler, Bağlantı Türleri, Sunucu Tipleri, GİB Yöntemleri)"
              />
            </Typography>
          </Alert>
        </Paper>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={preventGridImplicitSubmit}
          noValidate
          aria-busy={loading}
          sx={{
            minWidth: 0,
            '& .MuiFormLabel-root': {
              display: 'block',
              mb: 0.75,
              color: 'text.secondary',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.015em',
            },
            '& .MuiDataGrid-root': {
              borderColor: 'divider',
            },
          }}>
          <Collapse in={open} sx={{ mb: open ? 2.5 : 0 }}>
            <Alert
              severity="info"
              variant="outlined"
              action={
                <IconButton
                  sx={{ border: 'none' }}
                  aria-label={intl.formatMessage({ id: 'autocomplete.closeText', defaultMessage: 'Close' })}
                  color="inherit"
                  size="small"
                  onClick={hideAlertBox}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }>
              <Typography variant="body2">
                <FormattedMessage
                  id="book.modern.guide"
                  defaultMessage="Zorunlu bilgileri tamamlayın; tablo satırlarını düzenledikten sonra kaydetmeyi unutmayın."
                />
              </Typography>
            </Alert>
          </Collapse>

          <BookQuickNavigation />

          <Stack spacing={3}>
            <Box component="section" id="book-overview" sx={modernBookSectionSx}>
              <ModernBookSection kind="overview" />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <BookPanel
                    icon={<BusinessRoundedIcon />}
                    title={<FormattedMessage id="book.modern.panels.identity.title" defaultMessage="Proje Kimliği" />}
                    description={
                      <FormattedMessage
                        id="book.modern.panels.identity.description"
                        defaultMessage="Projeyi tanımlayan firma bilgisini girin."
                      />
                    }>
                    <FormLabel htmlFor="book-company-name" required>
                      <FormattedMessage id="project.companyName" defaultMessage="Firma" />
                    </FormLabel>
                    <Controller
                      name="companyName"
                      control={control}
                      rules={{ required: intl.formatMessage({ id: 'project.requiredCompanyCode' }) }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          id="book-company-name"
                          value={field.value ?? ''}
                          variant="outlined"
                          fullWidth
                          required
                          size="small"
                          autoComplete="organization"
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </BookPanel>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <BookPanel
                    icon={<ApartmentRoundedIcon />}
                    title={<FormattedMessage id="book.modern.panels.companies.title" defaultMessage="Şirketler" />}
                    description={
                      <FormattedMessage
                        id="book.modern.panels.companies.description"
                        defaultMessage="Proje kapsamındaki şirketleri ve vergi bilgilerini yönetin."
                      />
                    }
                    badge={
                      <FormattedMessage
                        id="book.modern.itemCount"
                        defaultMessage="{count} kayıt"
                        values={{ count: rows.length }}
                      />
                    }>
                    <StyledBox sx={{ minWidth: 0, maxWidth: '100%' }}>
                      <ZebraDataGrid
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={handleRowModesModelChange}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        localeText={renderDataGridLocaleText(intl)}
                        initialState={{ pinnedColumns: { right: ['actions'] } }}
                        slots={{ toolbar: EditToolbar }}
                        slotProps={{
                          toolbar: { setRows, setRowModesModel },
                        }}
                        hideFooter
                        disableColumnMenu
                        disableColumnSorting
                        disableRowSelectionOnClick
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                        disableAggregation
                        disableRowGrouping
                      />
                    </StyledBox>
                  </BookPanel>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <BookPanel
                    icon={<GroupsRoundedIcon />}
                    title={<FormattedMessage id="book.modern.panels.contacts.title" defaultMessage="İlgili Kişiler" />}
                    description={
                      <FormattedMessage
                        id="book.modern.panels.contacts.description"
                        defaultMessage="Efor bildirimlerinin alıcılarını ve iletişim bilgilerini yönetin."
                      />
                    }
                    badge={
                      <FormattedMessage
                        id="book.modern.itemCount"
                        defaultMessage="{count} kayıt"
                        values={{ count: rowsRelatedContacts.length }}
                      />
                    }>
                    <Stack spacing={2}>
                      <Controller
                        name="effortMailContactType"
                        control={control}
                        render={({ field }) => (
                          <Box component="fieldset" sx={{ minWidth: 0, m: 0, p: 0, border: 0 }}>
                            <FormLabel component="legend" id="book-effort-mail-contact-type-label">
                              <FormattedMessage
                                id="project.effortMailContactTypeLabel"
                                defaultMessage="Efor Sürecinde E-Posta Kime Gitsin?"
                              />
                            </FormLabel>
                            <RadioGroup
                              aria-labelledby="book-effort-mail-contact-type-label"
                              value={field.value}
                              onChange={(event) => field.onChange(Number(event.target.value))}
                              sx={{
                                mt: 0.5,
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'stretch', md: 'center' },
                                columnGap: 3,
                                rowGap: 0.25,
                                '& .MuiFormControlLabel-root': {
                                  minWidth: 0,
                                  m: 0,
                                },
                              }}>
                              <FormControlLabel
                                value={EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS}
                                control={<Radio />}
                                label={getEffortMailContactTypeTranslated(
                                  EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS
                                )}
                              />
                              <FormControlLabel
                                value={EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER}
                                control={<Radio />}
                                label={getEffortMailContactTypeTranslated(
                                  EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER
                                )}
                              />
                            </RadioGroup>
                          </Box>
                        )}
                      />

                      <Box
                        sx={{
                          minWidth: 0,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          '& > .MuiBox-root': {
                            width: '100% !important',
                            maxWidth: '100%',
                          },
                        }}>
                        <RelatedContactsCreateTable
                          rows={rowsRelatedContacts}
                          setRows={setRowsRelatedContacts}
                          rowModesModel={rowModesModelRelatedContacts}
                          setRowModesModel={setRowModesModelRelatedContacts}
                        />
                      </Box>
                    </Stack>
                  </BookPanel>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <BookPanel
                    icon={<Inventory2RoundedIcon />}
                    title={<FormattedMessage id="book.modern.panels.products.title" defaultMessage="Ürünler" />}
                    description={
                      <FormattedMessage
                        id="book.modern.panels.products.description"
                        defaultMessage="Ürün, ortam, sorumlu ve banka yapılandırmalarını tanımlayın."
                      />
                    }
                    badge={
                      <FormattedMessage
                        id="book.modern.itemCount"
                        defaultMessage="{count} kayıt"
                        values={{ count: rowsProducts.length }}
                      />
                    }>
                    <Box
                      sx={{
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        '& > .MuiBox-root': {
                          width: '100% !important',
                          maxWidth: '100%',
                        },
                      }}>
                      <ProductsCreateTable
                        rows={rowsProducts}
                        setRows={setRowsProducts}
                        rowModesModel={rowModesModelProducts}
                        setRowModesModel={setRowModesModelProducts}
                        products={products}
                        environmentTypes={environmentTypes}
                        users={users}
                        companies={companiesForProducts}
                        usedBanks={usedBanks}
                      />
                    </Box>
                  </BookPanel>
                </Grid>
              </Grid>
            </Box>

            <Box component="section" id="book-configuration" sx={modernBookSectionSx}>
              <ModernBookSection kind="configuration" />

              <BookPanel
                icon={<HubRoundedIcon />}
                title={
                  <FormattedMessage
                    id="book.modern.panels.configuration.title"
                    defaultMessage="Bağlantı Yapılandırması"
                  />
                }
                description={
                  <FormattedMessage
                    id="book.modern.panels.configuration.description"
                    defaultMessage="Bağlantı türünü ve GİB yöntemini seçin."
                  />
                }>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BookAutocomplete
                      id="book-connection-type"
                      label={<FormattedMessage id="project.connectType" defaultMessage="Bağlantı Türü" />}
                      options={connectionTypes}
                      loading={loadingConnectionTypes}
                      value={selectedConnectionType}
                      onChange={(newValue: ConnectionType | null) => {
                        setSelectedConnectionType(newValue);
                        setValue('connectTypeId', newValue ? newValue.id : null);
                      }}
                      error={Boolean(errors.connectTypeId)}
                      helperText={errors.connectTypeId?.message}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BookAutocomplete
                      id="book-gib-type"
                      label={<FormattedMessage id="project.gibType" defaultMessage="GİB Yöntemi" />}
                      options={gibTypes}
                      loading={loadingGibTypes}
                      value={selectedGibType}
                      onChange={(newValue: GibType | null) => {
                        setSelectedGibType(newValue);
                        setValue('gibTypeId', newValue ? newValue.id : null);
                      }}
                      error={Boolean(errors.gibTypeId)}
                      helperText={errors.gibTypeId?.message}
                    />
                  </Grid>
                </Grid>
              </BookPanel>
            </Box>

            <Box component="section" id="book-access" sx={modernBookSectionSx}>
              <ModernBookSection kind="access" />

              <BookPanel
                icon={<SecurityRoundedIcon />}
                title={<FormattedMessage id="book.modern.panels.access.title" defaultMessage="Erişim ve Entegrasyon" />}
                description={
                  <FormattedMessage
                    id="book.modern.panels.access.description"
                    defaultMessage="API, VPN, sunucu ve mali mühür bilgilerini kaydedin."
                  />
                }>
                <Grid container spacing={2}>
                  {accessFields.map((field) => (
                    <Grid key={field.name} size={{ xs: 12, md: 6 }}>
                      <BookTextAreaField control={control} {...field} />
                    </Grid>
                  ))}
                </Grid>
              </BookPanel>
            </Box>

            <Box component="section" id="book-sap" sx={modernBookSectionSx}>
              <ModernBookSection kind="sap" />

              <BookPanel
                icon={<LanRoundedIcon />}
                title={<FormattedMessage id="book.modern.panels.sap.title" defaultMessage="SAP Ortamları" />}
                description={
                  <FormattedMessage
                    id="book.modern.panels.sap.description"
                    defaultMessage="Geliştirme, test ve canlı ortam bilgilerini ayrı ayrı girin."
                  />
                }>
                <Grid container spacing={2}>
                  {sapFields.map((field) => (
                    <Grid key={field.name} size={{ xs: 12, md: 4 }}>
                      <BookTextAreaField control={control} {...field} />
                    </Grid>
                  ))}
                </Grid>
              </BookPanel>
            </Box>

            <Box component="section" id="book-notes" sx={modernBookSectionSx}>
              <ModernBookSection kind="notes" />

              <BookPanel
                icon={<NotesRoundedIcon />}
                title={<FormattedMessage id="book.modern.panels.notes.title" defaultMessage="Notlar" />}
                description={
                  <FormattedMessage
                    id="book.modern.panels.notes.description"
                    defaultMessage="Proje ekibinin bilmesi gereken ek ayrıntıları paylaşın."
                  />
                }>
                <BookTextAreaField
                  control={control}
                  name="note"
                  labelId="project.note"
                  defaultMessage="Notlar"
                  minRows={5}
                />
              </BookPanel>
            </Box>
          </Stack>

          <Paper elevation={0} sx={modernBookActionsSx}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between">
              <Typography id="book-actions-hint" variant="body2" color="text.secondary">
                <FormattedMessage
                  id="book.modern.actions.hint"
                  defaultMessage="Zorunlu alanları ve tablo satırlarını kontrol ederek projeyi oluşturun."
                />
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  startIcon={<RestartAltRoundedIcon />}
                  disabled={loading}
                  onClick={resetForm}
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}>
                  <FormattedMessage id="project.reset" defaultMessage="Temizle" />
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  startIcon={<CheckCircleRoundedIcon />}
                  disabled={loading}
                  aria-describedby="book-actions-hint"
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}>
                  <FormattedMessage id="project.create" defaultMessage="Oluştur" />
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      )}

      <LoadingBackdrop
        open={loading}
        sx={{ gap: 1.5 }}
        statusLabel={<FormattedMessage id="book.modern.loading" defaultMessage="Proje oluşturuluyor" />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          <FormattedMessage id="book.modern.loading" defaultMessage="Proje oluşturuluyor…" />
        </Typography>
      </LoadingBackdrop>
    </Box>
  );
};

export default CreateBook;
